import type { ToolCallError } from "@repo/app-types/api/interviewLivekitAgent";
import {
  IMPLICIT_END_CALL_TOOL_DISPLAY_NAME,
  IMPLICIT_END_CALL_TOOL_ID,
} from "@repo/app-types/entities/implicitEndCallTool";
import { KNOWLEDGE_RETRIEVAL_TOOL_ID } from "@repo/app-types/entities/knowledgeRetrievalTool";
import {
  getCallTransferToolDestination,
  getKnowledgeRetrievalToolDocumentTitle,
  type AdminApiRecordingToolActivity,
} from "@repo/app-types/entities/recordingToolActivity";
import { ToolType, type Tool } from "@repo/app-types/entities/tool";
import {
  TOOL_RESULT_QUERY_TOOL_DISPLAY_NAME,
  TOOL_RESULT_QUERY_TOOL_ID,
} from "@repo/app-types/entities/tools/toolResultQuery";
import { safeParseJSON } from "@repo/app-types/helpers/utilities";
import { PopoverArrow } from "@repo/ui/popover/arrow";
import { popoverStyle } from "@repo/ui/popover/style";
import { IconAlertTriangle, IconTools } from "@tabler/icons-react";
import { DateTime } from "luxon";
import type { FC, ReactNode } from "react";
import {
  Button,
  Dialog,
  DialogTrigger,
  Header,
  Heading,
  Popover,
} from "react-aria-components";

export const ToolActivityBlock: FC<{
  activity: AdminApiRecordingToolActivity;
  tools: readonly Tool[] | undefined;
}> = ({ activity, tools }) => {
  const tool = tools?.find((t) => t.id === activity.tool_id);
  const error = activity.output.error;

  return (
    <DialogTrigger>
      <Button className="group flex items-center space-x-2 text-left">
        {error ? (
          <IconAlertTriangle className="size-4 shrink-0 text-red-500" />
        ) : (
          <IconTools className="size-4 shrink-0 text-muted-foreground group-hover:text-indigo-800" />
        )}
        <div className="text-foreground group-hover:text-indigo-800">
          <ToolActivityDisplay activity={activity} tool={tool} />
        </div>
      </Button>

      <Popover
        className={popoverStyle().popover({
          className: "flex min-w-0 flex-col",
        })}
        placement="top"
      >
        <PopoverArrow />
        <ToolActivityDetailsDialog activity={activity} tool={tool} />
      </Popover>
    </DialogTrigger>
  );
};

const ToolActivityDisplay: FC<{
  activity: AdminApiRecordingToolActivity;
  tool: Tool | undefined;
}> = ({ activity, tool }): ReactNode => {
  // There is no "tool" for the built-in knowledge retrieval tool, just an ID.
  if (activity.tool_id === KNOWLEDGE_RETRIEVAL_TOOL_ID) {
    const documentTitle = getKnowledgeRetrievalToolDocumentTitle(activity);

    if (!documentTitle) {
      return "Unknown knowledge base document retrieved";
    }

    return (
      <>
        <span className="font-medium">{documentTitle}</span> knowledge base
        document retrieved
      </>
    );
  }

  if (activity.tool_id === IMPLICIT_END_CALL_TOOL_ID) {
    return (
      <>
        <span className="font-medium">
          {IMPLICIT_END_CALL_TOOL_DISPLAY_NAME}
        </span>{" "}
        {activity.output.is_end_of_call
          ? "tool ended the call"
          : "tool failed to end the call"}
      </>
    );
  }

  if (activity.tool_id === TOOL_RESULT_QUERY_TOOL_ID) {
    return (
      <>
        <span className="font-medium">
          {TOOL_RESULT_QUERY_TOOL_DISPLAY_NAME}
        </span>{" "}
        tool queried a stored result
      </>
    );
  }

  if (!tool) {
    return "Unknown tool used";
  }

  const error = activity.output.error;
  if (error) {
    return (
      <>
        <span className="font-medium">{tool.name}</span> tool failed
        {error.response_status ? ` with status ${error.response_status}` : null}
      </>
    );
  }

  switch (tool.type) {
    case ToolType.CALL_TRANSFER: {
      const destination = getCallTransferToolDestination(activity);

      return (
        <>
          <span className="font-medium">{tool.name}</span> tool transferred to{" "}
          <span className="font-medium">
            {destination || "unknown destination"}
          </span>
        </>
      );
    }
    case ToolType.CALL_END:
      return (
        <>
          <span className="font-medium">{tool.name}</span> tool ended the call
        </>
      );

    default:
      return (
        <>
          <span className="font-medium">{tool.name}</span> tool used
        </>
      );
  }
};

const ToolActivityDetailsDialog: FC<{
  activity: AdminApiRecordingToolActivity;
  tool: Tool | undefined;
}> = ({ activity, tool }) => {
  const toolId = activity.tool_id;
  const timestamp = DateTime.fromISO(activity.created_at);

  const renderToolName = () => {
    if (toolId === KNOWLEDGE_RETRIEVAL_TOOL_ID) {
      return "Retrieve knowledge base document";
    }

    if (toolId === IMPLICIT_END_CALL_TOOL_ID) {
      return IMPLICIT_END_CALL_TOOL_DISPLAY_NAME;
    }

    if (toolId === TOOL_RESULT_QUERY_TOOL_ID) {
      return TOOL_RESULT_QUERY_TOOL_DISPLAY_NAME;
    }

    if (tool) {
      return tool.name;
    }

    return "(Unknown tool)";
  };

  const renderToolContent = () => {
    if (toolId === KNOWLEDGE_RETRIEVAL_TOOL_ID) {
      const documentTitle = getKnowledgeRetrievalToolDocumentTitle(activity);

      return (
        <div>
          <h4 className="font-semibold">Title</h4>
          <p className="">{documentTitle || "Unknown"}</p>
        </div>
      );
    }

    if (toolId === IMPLICIT_END_CALL_TOOL_ID) {
      return (
        <p className="">
          {activity.output.is_end_of_call
            ? "The agent hung up the call."
            : "The agent tried to hang up the call, but it did not go through."}
        </p>
      );
    }

    if (toolId === TOOL_RESULT_QUERY_TOOL_ID) {
      return (
        <>
          <div>
            <h4 className="font-semibold">Query</h4>
            <p className="">
              {formatQuery(activity.input.tool_arguments.query)}
            </p>
          </div>

          <div>
            <h4 className="font-semibold">Answer</h4>
            <pre className="mt-1 rounded-sm bg-gray-100 p-2 text-xs whitespace-pre-wrap">
              {activity.output.content || "(No answer.)"}
            </pre>
          </div>
        </>
      );
    }

    switch (tool?.type) {
      case ToolType.CALL_TRANSFER: {
        const destination = getCallTransferToolDestination(activity);

        return (
          <div>
            <h4 className="font-semibold">Transfer destination</h4>
            <p className="">{destination || "Unknown"}</p>
          </div>
        );
      }

      case ToolType.CALL_END:
        return (
          <p className="">
            The call ended or was transferred back to the original phone
            provider.
          </p>
        );

      case ToolType.CUSTOM:
        return (
          <>
            <div>
              <h4 className="font-semibold">Input</h4>
              <pre className="mt-1 rounded-sm bg-gray-100 p-2 text-xs whitespace-pre-wrap">
                {JSON.stringify(activity.input.tool_arguments, null, 2)}
              </pre>
            </div>

            <div>
              <h4 className="font-semibold">Output</h4>
              <pre className="mt-1 rounded-sm bg-gray-100 p-2 text-xs whitespace-pre-wrap">
                {formatJSONish(activity.output.content || "")}
              </pre>
            </div>
          </>
        );

      default:
        return null;
    }

    return null;
  };

  return (
    <Dialog className="max-w-md space-y-4 overflow-y-auto px-4 py-3 text-sm">
      <Header>
        <Heading slot="title" className="truncate text-base font-semibold">
          {renderToolName()}
        </Heading>
        <div className="text-gray-600">
          {timestamp.toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS)}
        </div>
      </Header>

      <div className="space-y-2">
        {renderToolContent()}
        {activity.output.error ? (
          <ToolActivityError error={activity.output.error} />
        ) : null}
      </div>
    </Dialog>
  );
};

/**
 * The agent is only ever told a tool call went wrong, so without this the
 * failure lives in the server logs alone.
 */
const ToolActivityError: FC<{ error: ToolCallError }> = ({ error }) => (
  <div>
    <h4 className="font-semibold text-red-700">Error</h4>
    <p className="mt-1">
      {error.message}
      {error.response_status ? ` (status ${error.response_status})` : null}
    </p>
    {error.response_body ? (
      <pre className="mt-1 rounded-sm bg-gray-100 p-2 text-xs whitespace-pre-wrap">
        {formatJSONish(error.response_body)}
      </pre>
    ) : null}
  </div>
);

function formatQuery(query: unknown): string {
  return typeof query === "string" ? query : "Unknown";
}

/** Pretty-prints a JSON payload, leaving anything that is not JSON as it is. */
function formatJSONish(value: string): string {
  const parsed = safeParseJSON(value);

  return parsed === null ? value : JSON.stringify(parsed, null, 2);
}
